/**
 * „Wer an wen“ an der Blase — der Dreier-Modus. (16.10.2026, Martin)
 *
 * Martin: in den Chat-Blasen soll klein sichtbar sein, wer zu wem spricht („Martin → Schmied“, „Schmied → Martin“). Gezeigt wird
 * NUR, was belegt ist — das Feld `an` (shared/dreier-adressat.ts), wie es gespeichert (`metadata.an`) oder gesendet (Bus `an`)
 * wurde. Es wird nie geraten: fehlt es oder ist es ungültig, steht nichts da.
 *
 * Wo es heute belegt ist:
 *   - Martins Nachricht an den Schmied: `an: "schmied"` (server/chat.ts `persistUserEarly`, Chat wie Sprechtaste) — und live,
 *     sobald der Server `schmied` meldet (die Nachricht ging an ihn, app.ts);
 *   - der Beitrag des Schmieds: `an` und `sekundaer`, wenn er sie mitschickt (scripts/dreier-sagen.mjs `--an`).
 * Vesperas Antworten und Martins Nachrichten an Vespera tragen KEIN Feld — dort steht darum nichts.
 *
 * Barrierefreiheit: der Pfeil ist nur fürs Auge (`aria-hidden`); ein Bildschirmleser liest „Martin an Schmied“ (das Wort „an“ ist
 * nur für ihn, visuell verborgen). Aussehen: frontend/style.css `.bubble-adressat` (kein Inline-Style).
 */
import { adressatAus, martinsWahlAus, sekundaerAus, type Adressat } from "../../shared/dreier-adressat.js";
import { entwickler, istZusatzId, type ZusatzId } from "../../shared/entwickler.js";
import { SCHMIED_NAME } from "../../shared/schmied.js";

/** Wer spricht, an wen (und an wen noch). */
export interface AdressatVermerk {
  von: Adressat;
  an: Adressat;
  sekundaer?: Adressat;
}

/** So heißen sie in der Anzeige (wie im Faden, server/dreier-modus.ts; der Dreier-Modus gilt nur Martins Konto). */
const NAME: Readonly<Record<Adressat, string>> = {
  martin: "Martin",
  vespera: "Vespera",
  schmied: SCHMIED_NAME,
  zimmermann: entwickler("zimmermann").name,
  hueter: entwickler("hueter").name,
};

/**
 * Der Vermerk aus den belegten Feldern einer Zeile — oder `null`. `von`: Martin (eine Nachricht von ihm) oder der Schmied (sein
 * Beitrag); Vespera trägt kein Feld. Niemand spricht sich selbst an: dann `null`. Rein.
 */
export function adressatVermerkAus(von: "martin" | "schmied", felder: { an?: unknown; sekundaer?: unknown } | null | undefined): AdressatVermerk | null {
  if (von === "martin") {
    const an = martinsWahlAus(felder?.an);
    return an ? { von, an } : null;
  }
  const an = adressatAus(felder?.an);
  if (!an || an === von) return null;
  const sekundaer = sekundaerAus(felder?.sekundaer, an);
  return sekundaer && sekundaer !== von ? { von, an, sekundaer } : { von, an };
}

/**
 * Martins Nachricht ging an den ZIMMERMANN (17.10.2026): der Server meldet `zimmermann` (lib/chat-http.ts, lib/ptt.ts), app.ts setzt
 * „Martin → Zimmermann“ an die Blase. Anders als beim Schmied wartet nichts auf ihn — es gibt kein Mikro-Warten.
 */
export const ZIMMERMANN_ANGESPROCHEN = "voice-v8:zimmermann-angesprochen";

/**
 * 17.10.2026 (Martin 19:46): dasselbe Ereignis für JEDEN Zusatz-Entwickler (Zimmermann, Hüter) — `detail.id` sagt, wer; fehlt es,
 * war es der Zimmermann (die Form vor dem Hüter).
 */
export const ZUSATZ_ANGESPROCHEN = ZIMMERMANN_ANGESPROCHEN;

/** Aus dem Strom: die jüngste Nachricht Martins ging an diesen Zusatz-Entwickler. Wirft nie. */
export function meldeZusatzAngesprochen(id: ZusatzId): void {
  try {
    window.dispatchEvent(new CustomEvent(ZUSATZ_ANGESPROCHEN, { detail: { id } }));
  } catch {
    /* kein window (Tests) */
  }
}

/** Aus dem Strom: die jüngste Nachricht Martins ging an den Zimmermann. Wirft nie. */
export function meldeZimmermannAngesprochen(): void {
  meldeZusatzAngesprochen("zimmermann");
}

/** Wer angesprochen war — aus dem Ereignis; ohne gültige Angabe der Zimmermann. Rein. */
export function zusatzAusEreignis(ev: Event): ZusatzId {
  const id = (ev as CustomEvent<{ id?: unknown } | null>).detail?.id;
  return istZusatzId(id) ? id : "zimmermann";
}

/** Die Namen der Angesprochenen („Martin, Vespera“). */
function empfaengerNamen(v: AdressatVermerk): string {
  return (v.sekundaer ? [v.an, v.sekundaer] : [v.an]).map((a) => NAME[a]).join(", ");
}

/** Der lesbare Text („Martin → Schmied“, „Schmied → Martin, Vespera“). Rein. */
export function adressatText(v: AdressatVermerk): string {
  return `${NAME[v.von]} → ${empfaengerNamen(v)}`;
}

function teil(klasse: string, text: string): HTMLElement {
  const el = document.createElement("span");
  el.className = klasse;
  el.textContent = text;
  return el;
}

/**
 * Setzt den Vermerk an eine Blase (oder ersetzt ihn) — doppelt aufgerufen, steht er einmal. Bei der Blase des Entwicklers nach
 * seinem Namen (und dem Vorlese-Stand), bei Martins Blase nach dem Sprecher-Etikett, sonst als erster Kindknoten über dem Text.
 * Ohne Vermerk geschieht nichts.
 */
export function setzeAdressatVermerk(blase: HTMLElement, v: AdressatVermerk | null): void {
  if (!v) return;
  blase.querySelector(":scope > .bubble-adressat")?.remove();
  const el = teil("bubble-adressat", "");
  el.dataset.von = v.von;
  el.dataset.an = v.an;
  el.append(teil("bubble-adressat-name", NAME[v.von]));
  const pfeil = teil("bubble-adressat-pfeil", "→");
  pfeil.setAttribute("aria-hidden", "true");
  el.append(pfeil, teil("bubble-adressat-an", "an"));
  el.append(teil("bubble-adressat-name", empfaengerNamen(v)));
  el.title = adressatText(v);
  const vorgaenger =
    blase.querySelector(":scope > .bubble-entwickler-stand") ??
    blase.querySelector(":scope > .bubble-entwickler-name") ??
    blase.querySelector(":scope > .bubble-speaker");
  if (vorgaenger) vorgaenger.after(el);
  else blase.prepend(el);
}
